import { stdObject } from "../data/types/stdObject";
import { useTranslation } from "react-i18next";
import { convertDictData } from "../utils/convertDictData";
import { colors } from "../data/colors";
import { mainConfig } from "../data/main";
import { ChangeEvent, memo, useEffect, useRef, useState } from "react";
import QuickSearchComp from "./QuickSearchComp";
import BasicButton from "./BasicButton";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import NOPIC from "../assets/nopic.svg";

const BasicInput = memo(
  ({
    code = "",
    type = "text",
    refId = null,
    label = "",
    value = "",
    maxLength = undefined,
    active = true,
    autoFocus = false,
    onFocus,
    onBlur,
    onChange,
    onKeyDown,
    btnClick = () => {},
    innerStyle = {},
    style = {},
    placeholder = "",
    options = [],
    rangeParams = {},
    isTable = false,
    suffix = "",
    quickData = {},
    imageCode = "",
    imageFormat = "",
    imageFolder = "cat",
  }: {
    code?: string;
    type?: string;
    refId?: null;
    label?: string;
    value?: string;
    maxLength?: number | undefined;
    active?: boolean;
    innerStyle?: stdObject;
    style?: stdObject;
    autoFocus?: boolean;
    onFocus?: (v: any) => {};
    onBlur?: (v: any) => {};
    onChange?: (v: any) => {};
    onKeyDown?: (v: any) => {};
    btnClick?: (v?: any) => void;
    placeholder?: string;
    options?: Array<any>;
    rangeParams?: stdObject;
    isTable?: boolean | number;
    suffix?: string;
    quickData?: stdObject;
    imageCode?: string | number;
    imageFormat?: string;
    imageFolder?: string;
  }) => {
    const { t } = useTranslation();
    const [showPass, setShowPass] = useState(false);
    const refQuickPerson = useRef<HTMLInputElement>(null);
    const [quickPerson, setQuickPerson] = useState({
      code: "",
      open: false,
      search: "",
    });
    const loginRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
      if (autoFocus) {
        loginRef.current?.focus();
      }
    }, []);

    const imagePreview: any = value;
    const imageRemoved =
      imagePreview && typeof imagePreview === "object" && imagePreview.DELETE;
    const imageSrc = imageRemoved
      ? NOPIC
      : imagePreview && typeof imagePreview === "object" && imagePreview.BASE
        ? imagePreview.BASE
        : imageFormat
          ? `${mainConfig.url}/images/ART/${imageFolder}/${imageCode}.${imageFormat}`
          : NOPIC;
    const hasImage = !imageRemoved && imageSrc !== NOPIC;

    const handleImageChange = (e: ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (!files || files.length === 0 || !files[0].type.startsWith("image/"))
        return;

      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        onChange &&
          onChange({
            target: {
              value: {
                BASE: result,
                FORIMG: result.split(";")[0].split("/")[1],
              },
            },
          });
      };
      reader.readAsDataURL(files[0]);
      e.target.value = "";
    };

    const handleImageRemove = () => {
      onChange && onChange({ target: { value: { DELETE: true } } });
    };

    const showVisibility = () => {
      const Icon = showPass ? VisibilityIcon : VisibilityOffIcon;
      return (
        <Icon
          onClick={() => setShowPass(!showPass)}
          style={{
            color: showPass ? colors.settings : colors.grey,
            marginLeft: 5,
          }}
        />
      );
    };

    const insertInput = () => {
      switch (type) {
        case "info":
          return value ?? "";
        case "select":
          return (
            <select
              ref={refId}
              className="selectStyle"
              value={value}
              disabled={!active}
              style={{ border: `solid 1px ${colors.black}`, ...innerStyle }}
              onInput={onChange}
            >
              {options.map((e: stdObject, index: number) => (
                <option key={index} value={e.value}>
                  {e.label}
                </option>
              ))}
            </select>
          );
        case "textarea":
          return (
            <textarea
              ref={refId}
              className="textAreaStyle"
              disabled={!active}
              onChange={onChange}
              value={value}
              placeholder={placeholder}
              style={{
                width: "100%",
                boxSizing: "border-box",
                border: `solid 1px ${colors.black}`,
                ...innerStyle,
              }}
            />
          );
        case "dictionary":
          let dictData: stdObject = convertDictData(value);
          return "data" in dictData && Object.keys(dictData.data).length > 0 ? (
            <table className="dictTable">
              <tbody onClick={active ? onChange : () => {}}>
                {Object.keys(dictData.data).map((e: string, index: number) => (
                  <tr key={index}>
                    {dictData.hideCodes && (
                      <td valign="top">
                        <b>{e}</b>
                      </td>
                    )}
                    <td>{dictData.data[e].name}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div
              onClick={active ? onChange : () => {}}
              style={{
                padding: 3,
                paddingLeft: 5,
                paddingRight: 5,
                backgroundColor: colors.green,
                color: colors.white,
              }}
            >
              {t("p.select")}
            </div>
          );
        default:
          return (
            <span
              style={{
                display: type === "passwordInput" ? "flex" : "inline",
                flexDirection: "row",
                width: "100%",
              }}
            >
              <input
                ref={refId || loginRef}
                className="inputStyle"
                type={
                  type === "passwordInput"
                    ? showPass
                      ? "text"
                      : "password"
                    : type === "innerButton"
                      ? "button"
                      : type === "integer"
                        ? "number"
                        : type
                }
                maxLength={maxLength}
                value={value}
                checked={Number(value) === 1}
                disabled={!active}
                onFocus={onFocus}
                onBlur={onBlur}
                onChange={
                  ["checkbox", "button", "innerButton"].includes(type)
                    ? () => {}
                    : onChange
                }
                onClick={
                  ["checkbox", "button", "innerButton"].includes(type)
                    ? quickData.search
                      ? () =>
                          setQuickPerson({
                            ...quickPerson,
                            code: quickData.type,
                            open: true,
                          })
                      : onChange
                    : () => {}
                }
                onKeyDown={onKeyDown}
                placeholder={placeholder}
                style={{
                  width: type === "checkbox" ? "auto" : "100%",
                  border: `solid 1px ${colors.black}`,
                  ...innerStyle,
                }}
                {...(() => {
                  const { ...rest } = rangeParams;
                  return rest;
                })()}
              />
              {type === "passwordInput" && showVisibility()}
            </span>
          );
      }
    };

    const writeSuffix = () => (
      <span style={{ marginLeft: 5, marginTop: 3 }}>{suffix}</span>
    );

    return isTable ? (
      type === "image" ? (
        <tr style={{ ...style }}>
          <td style={{ position: "relative", whiteSpace: "nowrap" }}>
            <img
              src={imageSrc}
              onError={(ev: any) => (ev.target.src = NOPIC)}
              alt={label}
              style={{ width: 60, height: 60, objectFit: "contain" }}
            />
          </td>
          <td width={"100%"}>
            <input
              id={`imageInput-${code}`}
              type="file"
              accept="image/*"
              disabled={!active}
              style={{ display: "none" }}
              onChange={handleImageChange}
            />
            <div style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
              <label
                htmlFor={`imageInput-${code}`}
                className="stdButton noSelect"
                style={{ backgroundColor: colors.green, cursor: "pointer" }}
              >
                {t("p.uploadImage")}
              </label>
              {hasImage && active && (
                <BasicButton
                  Icon={DeleteForeverIcon}
                  color={colors.red}
                  style={{ marginLeft: 5 }}
                  onClick={handleImageRemove}
                />
              )}
            </div>
          </td>
        </tr>
      ) : ["label", "list", "tableButton"].includes(type) ? (
        <tr
          style={{
            backgroundColor: ["list", "tableButton"].includes(type)
              ? colors.white
              : colors.grey,
            color: ["list", "tableButton"].includes(type)
              ? colors.black
              : colors.white,
            ...style,
          }}
        >
          <td
            colSpan={typeof isTable === "number" ? isTable : 2}
            align={type === "list" ? "left" : "center"}
            style={{
              padding: 5,
              fontWeight: type === "tableButton" ? "normal" : "bolder",
            }}
          >
            {type === "list" && " • "}
            {type === "tableButton" ? (
              <BasicButton
                title={label}
                // Icon={e.Icon}
                color={innerStyle.color}
                onClick={btnClick}
              />
            ) : (
              label
            )}
          </td>
        </tr>
      ) : (
        <tr style={{ ...style }}>
          <td style={{ position: "relative", whiteSpace: "nowrap" }}>
            {label}
            {quickData.search && quickPerson.open && (
              <QuickSearchComp
                code={code}
                type={quickData.type}
                searchType={quickData.type}
                refInput={refQuickPerson}
                inputData={quickPerson}
                setInputData={setQuickPerson}
                quickData={quickData}
                updateFunction={onChange}
                newFunction={quickData.new}
                casualFunction={quickData.casual}
                cleanFunction={quickData.clean}
              />
            )}
          </td>
          <td width={"100%"}>
            {insertInput()}
            {suffix !== "" && writeSuffix()}
          </td>
        </tr>
      )
    ) : type === "image" ? (
      <div style={{ width: "100%", marginBottom: 3, ...style }}>
        <img
          src={imageSrc}
          onError={(ev: any) => (ev.target.src = NOPIC)}
          alt={label}
          style={{ width: 60, height: 60, objectFit: "contain" }}
        />
        <input
          id={`imageInput-${code}`}
          type="file"
          accept="image/*"
          disabled={!active}
          style={{ display: "none" }}
          onChange={handleImageChange}
        />
        <div style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
          <label
            htmlFor={`imageInput-${code}`}
            className="stdButton noSelect"
            style={{ backgroundColor: colors.green, cursor: "pointer" }}
          >
            {t("p.uploadImage")}
          </label>
          {hasImage && active && (
            <BasicButton
              Icon={DeleteForeverIcon}
              color={colors.red}
              style={{ marginLeft: 5 }}
              onClick={handleImageRemove}
            />
          )}
        </div>
      </div>
    ) : (
      <div style={{ width: "100%", marginBottom: 3, ...style }}>
        {label !== "" && <div className="inputLabel noSelect">{label}</div>}
        <div style={{ display: "flex", flexDirection: "row", width: "100%" }}>
          {insertInput()}
          {suffix !== "" && writeSuffix()}
        </div>
      </div>
    );
  },
);

export default BasicInput;
